import React, { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import BreadCrumsHeader from "../BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Checkbox from "../../common/Checkbox";
import Status from "../Status";
import Delete from "../Delete";
import Edit from "../Edit";
import Pagination from "../TablePagination";
import CreateNewButton from "../CreateNewButton";
import { ReactComponent as CheckedWhiteIcon } from "../../../Assets/Icons/checkedWhiteBox.svg";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedWhiteBox.svg";
import { ReactComponent as UncheckedGreyIcon } from "../../../Assets/Icons/unCheckedGreyIcon.svg";
import blueCross from "../../../Assets/Icons/blueCross.svg";
import whiteTick from "../../../Assets/Icons/White Tick.svg";
import { ReactComponent as Image } from "../../../Assets/Icons/Image.svg";
import { ReactComponent as LocationIcon } from "../../../Assets/Icons/Location.svg";
import { ReactComponent as ApprovedSubCategoryIcon } from "../../../Assets/Icons/approvedsubCategoryIcon.svg";
import { ReactComponent as SubCategoryIcon } from "../../../Assets/Icons/subCategoryIcon.svg";
import { ReactComponent as UnapprovedSubCatIcon } from "../../../Assets/Icons/unapprovedSubCatIcon.svg";
import { ReactComponent as ApprovedRulesIcon } from "../../../Assets/Icons/approvedRulesIcon.svg";
import { ReactComponent as RulesIcon } from "../../../Assets/Icons/rulesIcon.svg";
import { ReactComponent as UnapprovedRulesIcon } from "../../../Assets/Icons/unapprovedRulesIcon.svg";
import descriptionIcon from "../../../Assets/Icons/descriptionIcon.svg";
import {
  deleteOffer,
  getNonDraftedOffers,
  getOffers,
  getOfferById,
  getOffersByMerchant,
  toggleOfferStatus,
  approveOrRejectResources,
} from "../../../services/offerService";
import ImagePopup from "../ImagePopup";
import LocationPopup from "../LocationPopup";
import CancelPopup from "../CancelPopup";
import deleteImg from "../../../Assets/Images/deleteImg.svg";
import {
  separateDateTimeAndConvertToLocale,
} from "../../../utils/utilFunctions";
import { getS3PublicUrl } from "../../../utils/s3";
import Loader from "../../common/Loader";
import DetailsPopup from "../DetailsPopup";
import SubCategoryPopup from "../SubCategoryPopup";
import Confirmation from "./Confirmation";
import { DatePicker } from "antd";
import FilterButton from "../../common/FilterButton";
import infoIcon from "../../../Assets/Icons/infoIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
import SearchBar from "../SearchBar";
import debounce from "lodash.debounce";
import { Dropdown } from "antd";
import FilterPanel from "../FilterPanel";
import { getCategories } from "../../../services/offerService";
import DetailsModal from "../DetailsModal";
import TableEmptyState from "../TableEmptyState";
import PopupContent from "./PopupContent";
import "./offerList.scss";

const AdminOfferlist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.offerReducer?.loading);
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [deleteClicked, setDeleteClicked] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [clickedOffer, setClickedOffer] = useState(null);
  const [reason, setReason] = useState("");
  const [infoClicked, setInfoClicked] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);
  const [statusToggled, setStatusToggled] = useState(false);
  const [selected, setSelected] = useState([]);
  const [selectedMerchantIds, setSelectedMerchantIds] = useState([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [showSingleApprovalConfirm, setShowSingleApprovalConfirm] =
    useState(false);
  const [singleApprovalOfferId, setSingleApprovalOfferId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [fullOffersList, setFullOffersList] = useState([]);
  const [displayedOffers, setDisplayedOffers] = useState([]);

  // Persist filter selections across refreshes
  const STORAGE_KEY = "ADMIN_OFFERS_LIST_FILTERS_V2";

  const getInitialFilters = () => {
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        const params = saved.filterParams || null;
        // Migration: If duration was "DAY" (Today), clear it to null (All) by default
        if (params && params.duration === "DAY") {
          params.duration = null;
        }
        return params;
      }
    } catch (_) { }
    return null;
  };

  const getInitialSelections = () => {
    try {
      const savedRaw = localStorage.getItem(STORAGE_KEY);
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        const selections = saved.selections || null;
        // Migration: If duration was "Today", clear it to "null" (All) by default
        if (selections && selections.duration === "Today") {
          selections.duration = null;
        }
        return selections;
      }
    } catch (_) { }
    return null;
  };

  const initialFilters = getInitialFilters();
  const [customDate, setCustomDate] = useState(() => {
    if (initialFilters?.startDate || initialFilters?.endDate) {
      return [
        initialFilters.startDate ? dayjs(initialFilters.startDate) : null,
        initialFilters.endDate ? dayjs(initialFilters.endDate) : null
      ];
    }
    return [null, null];
  });

  const [filterParams, setFilterParams] = useState(initialFilters || {
    category: null,
    subCategory: [], // Initialize as empty array for multi-select
    status: null,
    duration: null,
    offerType: null,
    redemptionMethod: null,
    creatorRole: null,
  });

  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    getCategoriesData();
  }, []);


  // Add these mappings
  const DURATION_MAP = {
    "Today": "DAY",
    "This Week": "WEEK",
    "This Month": "MONTH",
    "This Year": "YEAR",
    "Custom Date": "CUSTOM",
  };

  const STATUS_MAP = {
    Approved: "approved",
    Declined: "declined",
    Pending: "pending",
    Drafted: "drafted",
    "In-Process": "in-process",
  };

  const OFFER_TYPE_MAP = {
    "Instore Swoodle Offer": "INSTORE_SWOODLE_OFFER", // Corrected as per your API
    Online: "ONLINE_OFFER", // Corrected as per your API
  };

  const REDEMPTION_METHOD_MAP = {
    "Swoodle Code Offer": "SWOODLE_CODE_OFFER", // Based on common redemption types matching your API's IN_STORE
    "QR Code Offer": "QR_CODE", // Assuming QR/Bar Code are in-store methods
    "Bar Code Offer": "BAR_CODE", // Assuming QR/Bar Code are in-store methods
  };

  // Helper function to filter approved subcategories
  const getApprovedSubcategories = (category) => {
    if (
      !category ||
      !category.subCategory ||
      !Array.isArray(category.subCategory)
    ) {
      return [];
    }

    return category.subCategory;
  };

  // Helper function to update subcategories when a category is selected
  const updateSubcategoriesForCategory = (selectedCategory) => {
    if (!selectedCategory) return;

    // Get approved subcategories
    const approvedSubcategories = getApprovedSubcategories(selectedCategory);

    // Reset subcategory selections
    setSelections((prev) => ({
      ...prev,
      subcategory: [], // Initialize as empty array for multi-select
    }));
  };

  // Update the getCategoriesData function to populate filterData
  const getCategoriesData = async () => {
    const categoryPromise = await dispatch(getCategories())?.unwrap();
    if (categoryPromise?.success === true) {
      const categoriesData = categoryPromise?.data;
      setCategories(categoriesData);

      // Update selections with the first category as default if available
      if (categoriesData.length > 0) {
        // Also update subcategories based on the first category
        updateSubcategoriesForCategory(categoriesData[0]);
      }
    }
  };
  // Initialize selections with the initial state OR rehydrated state
  const [selections, setSelections] = useState(getInitialSelections() || {
    duration: null,
    status: null,
    category: null,
    subcategory: [],
    offerType: null,
    redemptionMethod: null,
    creatorRole: null,
  });

  const subcategoryOptions = useMemo(() => {
    const selectedCategory = categories.find((cat) => cat._id === selections.category);
    return getApprovedSubcategories(selectedCategory);
  }, [categories, selections.category]);

  const filterData = useMemo(() => [
    {
      id: "duration",
      title: "Time Period",
      options: ["Today", "This Week", "This Month", "This Year", "Custom Date"],
      defaultValue: null,
      multiSelect: false,
      valueMap: DURATION_MAP,
      customContent: (selected) => {
        return selected === "Custom Date" ? (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-medium text-gray-600">Start Date</label>
              <DatePicker
                value={customDate?.[0]}
                onChange={(date) => {
                  const newDates = [date, customDate?.[1]];
                  setCustomDate(newDates);
                }}
                style={{ width: '100%' }}
                placeholder="Start Date"
                format="YYYY-MM-DD"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-medium text-gray-600">End Date</label>
              <DatePicker
                value={customDate?.[1]}
                onChange={(date) => {
                  const newDates = [customDate?.[0], date];
                  setCustomDate(newDates);
                }}
                style={{ width: '100%' }}
                placeholder="End Date"
                format="YYYY-MM-DD"
              />
            </div>
          </div>
        ) : null;
      }
    },
    {
      id: "status",
      title: "Status",
      options: ["Approved", "Declined", "Pending", "Drafted", "In-Process"],
      multiSelect: false,
      valueMap: STATUS_MAP,
    },
    {
      id: "category",
      title: "Category",
      options: categories,
      defaultValue: "",
      multiSelect: false,
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "subcategory",
      title: "Subcategory",
      options: subcategoryOptions,
      defaultValue: [],
      multiSelect: true,
      optionValueKey: "_id",
      optionLabelKey: "title",
    },
    {
      id: "offerType",
      title: "Offer Type",
      options: ["Instore Swoodle Offer", "Online"],
      multiSelect: false,
      valueMap: OFFER_TYPE_MAP,
    },
    {
      id: "redemptionMethod",
      title: "Redemption Method",
      options: ["Swoodle Code Offer", "QR Code Offer", "Bar Code Offer"],
      multiSelect: false,
      valueMap: REDEMPTION_METHOD_MAP,
    },
    {
      id: "creatorRole",
      title: "Created By",
      options: ["Admin", "Merchant"],
      multiSelect: false,
      valueMap: {
        Admin: "admin",
        Merchant: "merchant",
      },
    },
  ], [categories, subcategoryOptions, customDate, selections.duration]);

  // Create initial state logic (helper function)
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  // No longer need immediate rehydration in useEffect as we do it in initializers
  // which prevents the double API call on mount.
  useEffect(() => {
    // Only if somehow it's empty, we could fall back, but initializers are better.
  }, []);

  // Save filters whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ selections, filterParams })
      );
    } catch (_) { }
  }, [selections, filterParams]);

  // Use effect to initialize subcategories when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && selections.category) {
      const selectedCategory = categories.find(
        (cat) => cat._id === selections.category
      );
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  }, [categories, selections.category]);

  // Update the handleSelectionChange function
  const handleSelectionChange = (filterId, value) => {
    console.log("handleSelectionChange called");
    console.log("filterData ++>", filterData);
    console.log("filterId ++>", filterId);
    console.log("value ++>", value);
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));

    // If category changes, update subcategories
    if (filterId === "category") {
      // Find the selected category
      const selectedCategory = categories.find((cat) => cat._id === value);
      if (selectedCategory) {
        updateSubcategoriesForCategory(selectedCategory);
      }
    }
  };

  // Update the handleReset function
  const handleReset = () => {
    const initialState = createInitialState(filterData);
    setSelections(initialState);
    if (categories.length > 0) {
      updateSubcategoriesForCategory(categories[0]);
    }
    setFilterParams({
      category: null,
      subCategory: [],
      status: null,
      duration: null,
      offerType: null,
      redemptionMethod: null,
      creatorRole: null,
    });
    setCustomDate([null, null]);
    setSearchInput("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // When applying filters, you can now access both category ID and subcategory IDs array
  const handleApply = () => {
    handleCancel();
    console.log("Applying filters:", selections);

    // Create a new object for mapped filter parameters
    const mappedFilterParams = {};

    filterData.forEach((filter) => {
      const selectedValue = selections[filter.id];
      if (
        selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== "" &&
        !(Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        if (filter.id === "category") {
          mappedFilterParams[filter.id] = selectedValue; // Category ID is already correct
        } else if (filter.id === "subcategory") {
          mappedFilterParams[filter.id] = selectedValue; // Subcategory IDs array is correct
        } else if (filter.valueMap) {
          // Use the value map for other filters
          mappedFilterParams[filter.id] = filter.multiSelect
            ? selectedValue.map((val) => filter.valueMap[val]) // For multi-select, map each value
            : filter.valueMap[selectedValue]; // For single select, map the value
        } else {
          mappedFilterParams[filter.id] = selectedValue;
        }
      }
    });
    console.log("Mapped Filter Params:", mappedFilterParams);

    // Add custom date ranges if applicable
    if (selections.duration === "Custom Date") {
      if (customDate?.[0]) {
        mappedFilterParams.startDate = customDate[0].format("YYYY-MM-DD");
      }
      if (customDate?.[1]) {
        mappedFilterParams.endDate = customDate[1].format("YYYY-MM-DD");
      }
    }

    setFilterParams(mappedFilterParams);
    setOpen(false);
    setCurrentPage(1);
  };

  // Handle cancel button click
  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add("dropdown-open");
    } else {
      document.body.classList.remove("dropdown-open");
    }

    return () => {
      document.body.classList.remove("dropdown-open");
    };
  }, [open]);

  const items = [
    {
      title: "Offers",
    },
    {
      title: "Offers List",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Title",
    },
    {
      name: "Category Type",
    },
    {
      name: "Offer Type",
    },
    {
      name: "Discount Type",
    },
    {
      name: "Status",
    },
    {
      name: "Created By",
    },
    {
      name: "Actions",
    },
  ];

  const getOffersData = useCallback(async (silent = false) => {
    if (!silent) setSearching(true);
    const params = {
      limit: 200, // Reduced from 1000 to prevent backend timeouts
      search: searchQuery || undefined,
      category: filterParams.category || undefined,
      subcategory:
        filterParams.subcategory && filterParams.subcategory.length > 0
          ? filterParams.subcategory.join(",")
          : undefined,
      offerType: filterParams.offerType || undefined,
      status: filterParams.status
        ? (filterParams.status === "in-process" || filterParams.status === "drafted" ? "drafted" : filterParams.status)
        : undefined,
      duration: filterParams.duration === "CUSTOM" ? undefined : (filterParams.duration || undefined),
      redemptionMethod: filterParams.redemptionMethod || undefined,
      creatorRole: filterParams.status === "in-process"
        ? "merchant"
        : (filterParams.status === "drafted" ? "admin" : (filterParams.creatorRole || undefined)),
      silent: silent,
    };

    if (filterParams.duration === "CUSTOM" && customDate && (customDate[0] || customDate[1])) {
      params.startDate = customDate[0]?.format("YYYY-MM-DD");
      params.endDate = customDate[1]?.format("YYYY-MM-DD");
    }
    // Also include dates if they are already in filterParams (from localStorage)
    if (filterParams.startDate) params.startDate = filterParams.startDate;
    if (filterParams.endDate) params.endDate = filterParams.endDate;

    Object.keys(params).forEach(
      (key) => (params[key] === undefined ? delete params[key] : null)
    );
    try {
      const response = await dispatch(getNonDraftedOffers(params)).unwrap();
      if (response?.success) {
        setFullOffersList(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setSearching(false);
    }
  }, [filterParams, searchQuery, customDate, dispatch]);

  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    setDisplayedOffers(data.slice(startIndex, endIndex));
  };

  // 1. Initial/Filter/Search load
  useEffect(() => {
    getOffersData();
  }, [filterParams, searchQuery, deleteSuccess, statusToggled]);

  // 2. Logic to filter and slice data for display
  useEffect(() => {
    if (!fullOffersList) return;

    let filtered = [...fullOffersList];

    // Search Filter
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((offer) => {
        const title = (offer?.title || "").toLowerCase();
        const categoryType = (offer?.category?.title || "").toLowerCase();
        const businessName = (offer?.merchant?.businessName || offer?.merchant?.business_name || "").toLowerCase();

        // Search against the mapped "Offer Type" display strings
        let offerType = "";
        if (offer?.offerType === "ONLINE_OFFER") offerType = "online offer";
        else if (offer?.offerType === "INSTORE_SWOODLE_OFFER") offerType = "instore swoodle offer";

        // Search against the mapped "Discount Type" display strings
        let discountType = "";
        switch (offer?.offerDiscountType) {
          case "FREE_OFFER": discountType = "free offer"; break;
          case "DISCOUNTED_PERCENT": discountType = "discounted percent"; break;
          case "FIXED_DOLLAR": discountType = "fixed dollar"; break;
          case "BUY_ONE_GET_ONE_FREE": discountType = "buy one get one free"; break;
          case "BUNDLE_OFFER": discountType = "bundle offer"; break;
          default: discountType = "n/a";
        }

        // Search against the mapped "Created By" display strings
        const createdBy = (offer?.creatorRole?.toLowerCase() === "admin" ? "admin" : "merchant");

        return (
          title.includes(term) ||
          categoryType.includes(term) ||
          businessName.includes(term) ||
          offerType.includes(term) ||
          discountType.includes(term) ||
          createdBy.includes(term)
        );
      });
    }

    // Date Filter (Local)
    if (filterParams.duration && filterParams.duration !== "CUSTOM") {
      let start = null;
      let end = dayjs().endOf('day'); // Default end is today

      if (filterParams.duration === "DAY") {
        start = dayjs().startOf('day');
        end = dayjs().endOf('day');
      } else if (filterParams.duration === "WEEK") {
        start = dayjs().startOf('week');
      } else if (filterParams.duration === "MONTH") {
        start = dayjs().startOf('month');
      } else if (filterParams.duration === "YEAR") {
        start = dayjs().startOf('year');
      }

      if (start) {
        filtered = filtered.filter((offer) => {
          const createdAt = offer?.createdAt || offer?.created_at;
          if (!createdAt) return false;
          const joinedDate = dayjs(createdAt);
          return (joinedDate.isAfter(start) || joinedDate.isSame(start)) &&
            (joinedDate.isBefore(end) || joinedDate.isSame(end));
        });
      }
    }

    if (filterParams.duration === "CUSTOM" && (filterParams.startDate || filterParams.endDate)) {
      const start = filterParams.startDate ? dayjs(filterParams.startDate).startOf('day') : null;
      const end = filterParams.endDate ? dayjs(filterParams.endDate).endOf('day') : null;

      filtered = filtered.filter((offer) => {
        const createdAt = offer?.createdAt || offer?.created_at;
        if (!createdAt) return false;

        const joinedDate = dayjs(createdAt);
        if (!joinedDate.isValid()) return false;

        let matches = true;
        if (start) matches = matches && (joinedDate.isAfter(start) || joinedDate.isSame(start));
        if (end) matches = matches && (joinedDate.isBefore(end) || joinedDate.isSame(end));
        return matches;
      });
    }

    // Default Sort: Newest First
    filtered.sort((a, b) => {
      const dateA = dayjs(a?.createdAt || a?.created_at || 0);
      const dateB = dayjs(b?.createdAt || b?.created_at || 0);
      return dateB.diff(dateA);
    });

    setCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / 10) || 1);

    updatePageSlice(filtered, currentPage);
  }, [searchQuery, fullOffersList, filterParams, currentPage]);

  // 3. Page change effect
  useEffect(() => {
    if (fullOffersList.length > 0) {
      let filtered = [...fullOffersList];
      if (searchQuery.trim()) {
        const term = searchQuery.toLowerCase().trim();
        filtered = filtered.filter((offer) => {
          const title = (offer?.title || "").toLowerCase();
          const categoryType = (offer?.category?.title || "").toLowerCase();
          const businessName = (offer?.merchant?.businessName || "").toLowerCase();

          let offerType = "";
          if (offer?.offerType === "ONLINE_OFFER") offerType = "online offer";
          else if (offer?.offerType === "INSTORE_SWOODLE_OFFER") offerType = "instore swoodle offer";

          let discountType = "";
          switch (offer?.offerDiscountType) {
            case "FREE_OFFER": discountType = "free offer"; break;
            case "DISCOUNTED_PERCENT": discountType = "discounted percent"; break;
            case "FIXED_DOLLAR": discountType = "fixed dollar"; break;
            case "BUY_ONE_GET_ONE_FREE": discountType = "buy one get one free"; break;
            case "BUNDLE_OFFER": discountType = "bundle offer"; break;
            default: discountType = "";
          }

          const createdBy = (offer?.creatorRole?.toLowerCase() === "admin" ? "admin" : "merchant");

          return (
            title.includes(term) ||
            categoryType.includes(term) ||
            businessName.includes(term) ||
            offerType.includes(term) ||
            discountType.includes(term) ||
            createdBy.includes(term)
          );
        });
      }
      updatePageSlice(filtered, currentPage);
    }
  }, [currentPage]);

  // Update your handleSearchChange to simply set the state
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setCurrentPage(1);
        setSearching(false);
      }, 500),
    []
  );

  const handleSearchChange = (value) => {
    setSearchInput(value);
    setSearching(true);
    debouncedSearch(value);
  };
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of offer list when changing pages
    const container = document.getElementById('offer-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleImageIconClick = (offer) => {
    setClickedOffer(offer);
    setIsImageOpen(true);
  };

  const handleCloseImage = () => {
    setIsImageOpen(false);
    setClickedOffer(null);
  };

  const handleLocationClick = (offer) => {
    setClickedOffer(offer);
    setIsLocationOpen(true);
  };

  const handleDescriptionClick = (offer) => {
    setClickedOffer(offer);
    setIsDescriptionOpen(true);
  };

  const handleSubCategoryClick = (offer) => {
    setClickedOffer(offer);
    setIsSubCategoryOpen(true);
  };

  const handleRulesClick = (offer) => {
    setClickedOffer(offer);
    setIsRulesOpen(true);
  };

  const handleCloseLocation = () => {
    setIsLocationOpen(false);
    setClickedOffer(null);
  };

  const handleCloseDescription = () => {
    setIsDescriptionOpen(false);
    setClickedOffer(null);
  };

  const handleCloseSubCategory = () => {
    setIsSubCategoryOpen(false);
    setClickedOffer(null);
  };

  const handleCloseRules = () => {
    setIsRulesOpen(false);
    setClickedOffer(null);
  };

  const handleEditClick = (id) => {
    navigate("/admin/offer-form", {
      state: {
        id: id,
      },
    });
  };

  const handleDeleteClick = (offer) => {
    setDeleteClicked(true);
    setClickedOffer(offer);
  };

  const handleKeepOffer = () => {
    setDeleteClicked(false);
    setClickedOffer(null);
  };

  const handleDeleteOffer = async () => {
    closePopup();
    setDeleteClicked(false);
    if (clickedOffer?._id !== null) {
      const deletePromise = await dispatch(
        deleteOffer({ id: clickedOffer?._id, merchantId: clickedOffer?.merchant?._id })
      )?.unwrap();
      if (deletePromise?.success === true) {
        setDeleteSuccess(!deleteSuccess);
        setClickedOffer(null);
      }
    }
  };

  const handleCloseInfo = () => {
    setInfoClicked(false);
    setClickedOffer(null);
  };

  const handleInfoClick = (offer) => {
    setClickedOffer(offer);
    setInfoClicked(true);
  };



  const fetchOfferDetails = async (offerId) => {
    try {
      const offerDetailsPromise = await dispatch(
        getOfferById({ id: offerId, silent: true })
      )?.unwrap();
      if (offerDetailsPromise?.success === true) {
        setOfferDetails(offerDetailsPromise?.data);
      }
    } catch (error) {
      console.error("Error fetching offer details:", error);
    }
  };

  const openPopup = (offerId) => {
    setOfferDetails(null);
    setIsDetailOpen(true);
    fetchOfferDetails(offerId);
  };

  const closePopup = () => {
    setIsDetailOpen(false);
  };

  // Open direct from navigation state (e.g., notifications deep-link)
  useEffect(() => {
    const stateOfferId = location?.state?.openOfferId || location?.state?.offerId;
    if (stateOfferId) {
      openPopup(stateOfferId);
      // Clear state to avoid reopening on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location?.state?.openOfferId, location?.state?.offerId, location.pathname, navigate, openPopup]);

  // Get only selectable offers (Pending, Approved, Declined)
  const getSelectableOffers = () => {
    return (fullOffersList || []).filter(offer => isSelectable(offer.status));
  };

  const handleSelectAll = () => {
    const selectableOffers = getSelectableOffers();

    if (
      selectableOffers.length > 0 &&
      selected.length === selectableOffers.length
    ) {
      setSelected([]);
      setSelectedMerchantIds([]);
    } else {
      const newSelected = selectableOffers.map((offer) => offer._id);
      const newSelectedMerchantIds = selectableOffers.map(
        (offer) => offer?.merchant?._id
      );
      setSelected(newSelected);
      setSelectedMerchantIds(newSelectedMerchantIds);
    }
  };

  const handleSelectOne = (event, id, status) => {
    if (!isSelectable(status)) return;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    let newSelectedMerchantIds = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
      const offer = fullOffersList?.find((offer) => offer._id === id);
      const merchantId = offer?.merchant?._id;

      if (merchantId) {
        newSelectedMerchantIds = [...selectedMerchantIds, merchantId];
      } else {
        newSelectedMerchantIds = [...selectedMerchantIds];
      }
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);

      const offer = fullOffersList?.find((offer) => offer._id === id);
      const merchantId = offer?.merchant?._id;

      if (merchantId) {
        newSelectedMerchantIds = selectedMerchantIds.filter(
          (mId) => mId !== merchantId
        );
      } else {
        newSelectedMerchantIds = [...selectedMerchantIds];
      }
    }

    setSelected(newSelected);
    setSelectedMerchantIds(newSelectedMerchantIds);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Only Pending, Approved, and Declined statuses are selectable for bulk actions
  const isSelectable = (status) => {
    const s = status?.toLowerCase();
    return ["pending", "approved", "declined"].includes(s);
  };

  const handleBulkApprove = () => {
    setShowApprovalConfirm(true);
  };

  const handleApproveConfirm = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "approved",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        setStatusToggled(!statusToggled);
        setShowApprovalConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error approving offers:", error);
    }
  };

  const handleBulkDecline = () => {
    setShowDeclineConfirm(true);
  };

  // Move selected offers to pending
  const handleBulkPending = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "pending",
        reason: "Moved to pending",
        // Provide email bodies to satisfy backend validation
        TextBody: "Your offer status has been changed to pending.",
        HtmlBody: "<p>Your offer status has been changed to <strong>pending</strong>.</p>",
        // Avoid showing warning toast if backend still returns the email-body message
        silent: true,
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        // Optimistic UI: update selected rows in local cache to pending immediately
        setFullOffersList((prev) =>
          (prev || []).map((o) =>
            selected.includes(o._id) ? { ...o, status: "pending" } : o
          )
        );
        setStatusToggled(!statusToggled);
        setSelected([]);
        setSelectedMerchantIds([]);
        setShowActionModal(false);
        // Also re-fetch in the background to stay in sync
        try { getOffersData(); } catch { }
      }
    } catch (error) {
      console.error("Error setting pending offers:", error);
    }
  };

  const handleDeclineConfirm = async () => {
    try {
      const payload = {
        type: "offer",
        ids: selected,
        merchantId: selectedMerchantIds,
        status: "declined",
        reason: "Bulk declined",
      };
      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success) {
        setStatusToggled(!statusToggled);
        setShowDeclineConfirm(false);
        setSelected([]);
        setSelectedMerchantIds([]);
      }
    } catch (error) {
      console.error("Error declining offers:", error);
    }
  };

  const handleDeclineCancel = () => {
    setShowDeclineConfirm(false);
  };
  const handleApproveCancel = () => {
    setShowApprovalConfirm(false);
  };

  const handleSingleApprovalConfirm = async () => {
    if (singleApprovalOfferId) {
      try {
        const payload = {
          type: "offer",
          ids: [singleApprovalOfferId],
          status: "approved",
        };
        const statusPromise = await dispatch(
          approveOrRejectResources(payload)
        )?.unwrap();
        if (statusPromise?.success) {
          setShowSingleApprovalConfirm(false);
          setSingleApprovalOfferId(null);
          setIsDetailOpen(false); // Close the details modal
          setStatusToggled(!statusToggled);
        }
      } catch (error) {
        console.error("Error approving offer:", error);
      }
    }
  };

  const handleSingleApprovalCancel = () => {
    setShowSingleApprovalConfirm(false);
    setSingleApprovalOfferId(null);
  };

  const handleSingleApprovalClick = (offerId) => {
    setIsDetailOpen(false);
    setSingleApprovalOfferId(offerId);
    setShowSingleApprovalConfirm(true);
  };

  useEffect(() => {
    if (selected.length > 0) {
      setShowActionModal(true);
    } else {
      setShowActionModal(false);
    }
  }, [selected]);

  // Calculate count of selectable items for "Select All" checkbox
  const selectableCount = getSelectableOffers().length;
  const allSelectableSelected =
    selectableCount > 0 && selected.length === selectableCount;

  // Determine which bulk action buttons to show based on selected statuses
  const selectedStatuses = selected
    .map((id) => fullOffersList?.find((o) => o._id === id)?.status)
    .filter(Boolean)
    .map((s) => s?.toLowerCase());
  const hasPendingSelected = selectedStatuses.includes("pending");
  const showPendingOnly = selected.length > 0 && !hasPendingSelected; // all selected are approved/declined

  console.log("offerDetails", offerDetails);
  const handleApproveClickDirect = async (offerId) => {
    closePopup();
    try {
      // Get the offer details to access merchant ID
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "approved",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: "",
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error approving offer:", error);
    }
  };

  const handleDeclineClick = async (offerId, reason) => {
    closePopup();
    try {
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: "declined",
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: reason,
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        setIsDetailOpen(false); // Close the details modal
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error declining offer:", error);
    }
  };

  // Handle status click to change to pending
  const handleStatusClick = async (offerId, newStatus) => {
    try {
      const offer = fullOffersList?.find((offer) => offer._id === offerId);
      if (!offer) {
        console.error("Offer not found");
        return;
      }

      const payload = {
        type: "offer",
        status: newStatus,
        ids: [offerId],
        merchantId: offer?.merchant?._id,
        reason: `Changed from ${offer.status} to ${newStatus}`,
        ...(newStatus === "pending"
          ? {
            TextBody: `Your offer "${offer?.title || ""}" has been moved back to Pending for review.`,
            HtmlBody: `<p>Your offer <strong>${offer?.title || ""}</strong> has been moved back to <strong>Pending</strong> for review.</p>`,
          }
          : {}),
      };

      const statusPromise = await dispatch(
        approveOrRejectResources(payload)
      )?.unwrap();
      if (statusPromise?.success === true) {
        setStatusToggled(!statusToggled);
        getOffersData(); // Refresh the offers data
      }
    } catch (error) {
      console.error("Error changing offer status:", error);
    }
  };

  return (
    <>
      {(loading || searching) && <Loader />}
      {clickedOffer?.thumbnailImage !== null && isImageOpen === true && (
        <ImagePopup
          image={getS3PublicUrl(clickedOffer?.thumbnailImage)}
          handleCloseImage={handleCloseImage}
        />
      )}
      {isLocationOpen === true && (
        <LocationPopup
          locations={clickedOffer?.tempLocations}
          handleCloseLocation={handleCloseLocation}
        />
      )}
      {clickedOffer?.description !== "" && isDescriptionOpen === true && (
        <DetailsPopup
          content={clickedOffer?.description}
          handleClose={handleCloseDescription}
          icon={descriptionIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      {clickedOffer?.subCategory?.length > 0 && isSubCategoryOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.subCategory}
          handleCloseSubCategory={handleCloseSubCategory}
          icon={<SubCategoryIcon className="w-[10%]" />}
          heading="Subcategories"
        />
      )}
      {clickedOffer?.ruleAndRegulation?.length > 0 && isRulesOpen === true && (
        <SubCategoryPopup
          subCategories={clickedOffer?.ruleAndRegulation}
          handleCloseSubCategory={handleCloseRules}
          icon={<RulesIcon className="w-[10%]" />}
          heading="Rules and Regulations"
        />
      )}

      {/* Updated PopupContent with dynamic redemption codes */}
      {isDetailOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ animation: 'fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideIn {
                from {
                  opacity: 0;
                  transform: scale(0.95) translateY(-20px);
                }
                to {
                  opacity: 1;
                  transform: scale(1) translateY(0);
                }
              }
            `}
          </style>
          <div style={{ animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            <PopupContent
              offerDetails={offerDetails}
              handleDeleteClick={handleDeleteClick}
              handleEditClick={handleEditClick}
              handleSingleApprovalClick={handleApproveClickDirect}
              handleDeclineClick={handleDeclineClick}
              onClose={closePopup}
              onDuplicated={getOffersData}
            />
          </div>
        </div>
      )}

      {showApprovalConfirm && (
        <CancelPopup
          handleNo={handleApproveCancel}
          handleYes={handleApproveConfirm}
          heading="Approve Offers"
          content="Do you really want to approve all selected offers?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showDeclineConfirm && (
        <CancelPopup
          handleNo={handleDeclineCancel}
          handleYes={handleDeclineConfirm}
          heading="Decline Offers"
          content="Do you really want to decline all selected offers?"
          confirmation={false}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}
      {showSingleApprovalConfirm && (
        <CancelPopup
          handleNo={handleSingleApprovalCancel}
          handleYes={handleSingleApprovalConfirm}
          heading="Approve Offer"
          content="Do you really want to approve this offer?"
          confirmation={true}
          popupClassName="w-[300px] sm:w-[420px] h-[224px] sm:h-[220px]"
        />
      )}

      <div className="flex flex-col sm:flex-row justify-between col-span-12 gap-4">
        <BreadCrumsHeader heading="Offers List" items={items} />
      </div>
      {deleteClicked === true ? (
        <CancelPopup
          handleNo={handleKeepOffer}
          handleYes={handleDeleteOffer}
          heading={"Delete Offer"}
          content={"Are you sure you want to delete this offer?"}
          image={deleteImg}
        />
      ) : null}
      {infoClicked === true && (
        <DetailsPopup
          content={clickedOffer?.reason}
          handleClose={handleCloseInfo}
          icon={infoIcon}
          popupClassName={"w-[335px] sm:w-[470px] pb-5"}
        />
      )}
      <div className="w-full flex justify-between items-center mt-6 gap-3">
        <SearchBar
          parentDivClass="w-full sm:w-[60%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none rounded-[5px] border border-[#D9D9D9] focus:border-[#00ADEF]"
          iconClassName="absolute right-[10px] top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
          placeholder="Search"
          value={searchInput}
          handleChange={(e) => handleSearchChange(e.target.value)}
          loading={searching}
        />
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: "280px",
            width: "max-content",
            maxWidth: "90vw",
            maxHeight: "80vh",
            zIndex: 1000,
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          <FilterButton isOpen={open} />
        </Dropdown>
      </div>
      <div id="offer-list-container" className="col-span-12 mt-4">
        {/* Desktop Table */}
        <TableContainer style={{ width: "100%", overflowX: "auto" }} className="hidden md:block">
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                <TableCell className="!border-b-0">
                  <Checkbox
                    checked={allSelectableSelected}
                    onChecked={handleSelectAll}
                    divClassName="flex items-center"
                    UncheckedIcon={UncheckedIcon}
                    CheckedIcon={CheckedWhiteIcon}
                    checkClassName={"!w-[22px] !h-[22px]"}
                  />
                </TableCell>
                {columns.map((column) => {
                  return (
                    <TableCell key={column.name} className="!text-white !font-semibold" style={{ color: 'white !important' }}>
                      {column.name}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {displayedOffers && displayedOffers.length > 0 &&
                displayedOffers.map((offer, index) => {
                  const isItemSelected = isSelected(offer._id);
                  const itemSelectable = isSelectable(offer.status);
                  return (
                    <TableRow
                      className="h-[50px]"
                      sx={{
                        boxShadow: "0px 4px 4px 0px #00000040",
                        backgroundColor:
                          (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                      }}
                      key={offer._id}
                    >
                      <TableCell>
                        {itemSelectable && (
                          <Checkbox
                            checked={isItemSelected}
                            onChecked={(event) =>
                              handleSelectOne(event, offer._id, offer.status)
                            }
                            divClassName="flex items-center"
                            checkClassName="!w-[22px] !h-[22px]"
                          />
                        )}
                      </TableCell>
                      <TableCell className="max-w-[50px]">{(currentPage - 1) * 10 + index + 1}</TableCell>
                      <TableCell
                        title={offer?.title}
                        className="max-w-[100px] truncate"
                      >
                        {offer?.title || "N/A"}
                      </TableCell>
                      <TableCell
                        title={offer?.category?.title}
                        className="max-w-[80px] truncate"
                      >
                        {offer?.category?.title || "N/A"}
                      </TableCell>
                      <TableCell className="max-w-[180px]">
                        {(() => {
                          switch (offer?.offerType) {
                            case "ONLINE_OFFER":
                              return "ONLINE OFFER";
                            case "INSTORE_SWOODLE_OFFER":
                              return "INSTORE SWOODLE OFFER";
                            default:
                              return "N/A";
                          }
                        })()}
                      </TableCell>
                      <TableCell
                        title={offer?.offerDiscountType}
                        className="max-w-[80px] truncate"
                      >
                        {(() => {
                          switch (offer?.offerDiscountType) {
                            case "FREE_OFFER":
                              return "FREE OFFER";
                            case "DISCOUNTED_PERCENT":
                              return "DISCOUNTED PERCENT";
                            case "FIXED_DOLLAR":
                              return "FIXED DOLLAR";
                            case "BUY_ONE_GET_ONE_FREE":
                              return "BUY ONE GET ONE FREE";
                            case "BUNDLE_OFFER":
                              return "BUNDLE OFFER";
                            default:
                              return "N/A";
                          }
                        })()}
                      </TableCell>
                      <TableCell className="max-w-[110px]">
                        <Confirmation
                          status={offer?.status}
                          onStatusClick={handleStatusClick}
                          offerId={offer?._id}
                          enablePendingAction={true}
                          handleInfoClick={() => handleInfoClick(offer)}
                          creatorRole={offer?.creatorRole}
                        />
                      </TableCell>
                      <TableCell className="max-w-[100px] truncate">
                        {offer?.creatorRole === "admin" ? "Admin" : (offer?.creatorRole === "merchant" ? "Merchant" : "N/A")}
                      </TableCell>
                      <TableCell className="max-w-[120px]">
                        <button
                          className="flex justify-center items-center border border-[#00ADEF] rounded-[6px] p-3 h-[34px]"
                          onClick={() => openPopup(offer?._id)}
                        >
                          <span className="font-[600] text-[14px] font-poppins leading-[22px] text-[#00ADEF]">
                            View Details
                          </span>
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {!loading && !searching && displayedOffers && displayedOffers.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    align="center"
                    className="!border-b-0 !p-0"
                  >
                    <TableEmptyState itemName="Offer" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile View */}
        <div className="md:hidden flex flex-col gap-4">
          {displayedOffers && displayedOffers.length > 0 &&
            displayedOffers.map((offer) => (
              <div
                key={offer._id}
                className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm flex flex-col gap-3"
              >
                <div className="flex justify-between items-start text-xs">
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-[14px] font-[600] text-[#303030] truncate pr-2" title={offer?.title}>
                      {offer?.title || "N/A"}
                    </span>
                    <span className="text-[12px] text-[#767676] truncate pr-2">
                      {offer?.category?.title || "N/A"}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    <Confirmation
                      status={offer?.status}
                      onStatusClick={handleStatusClick}
                      offerId={offer?._id}
                      enablePendingAction={true}
                      handleInfoClick={() => handleInfoClick(offer)}
                      creatorRole={offer?.creatorRole}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[13px] font-poppins text-[#303030]">
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">Offer Type</span>
                    <span className="truncate">
                      {offer?.offerType === "ONLINE_OFFER" ? "Online" : "In-store"}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[#8C8C8C] text-[11px]">Created By</span>
                    <span className="truncate">
                      {offer?.creatorRole === "admin" ? "Admin" : (offer?.merchant?.businessName || "Merchant")}
                    </span>
                  </div>
                </div>

                <button
                  className="w-full mt-2 flex justify-center items-center border border-[#00ADEF] rounded-[6px] py-2 bg-[#F0FBFF] hover:bg-[#E0F7FA] transition-colors"
                  onClick={() => openPopup(offer?._id)}
                >
                  <span className="font-[600] text-[13px] text-[#00ADEF] font-poppins">
                    View Details
                  </span>
                </button>
              </div>
            ))
          }
          {!loading && !searching && (!displayedOffers || displayedOffers.length === 0) && (
            <TableEmptyState itemName="Offer" />
          )}
        </div>

        {displayedOffers && displayedOffers.length > 0 && (
          <Pagination
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
          />
        )}

        {/* Action Modal */}
        {
          showActionModal && (
            <div
              className="fixed bottom-16 left-[57%] transform -translate-x-1/2 bg-white rounded-[6px] px-4 h-[60px] flex items-center gap-4 z-50 text-[14px] font-[600] text-[#303030] border border-[#0000001A]"
              style={{ boxShadow: "0px 4px 20px 0px #00000029" }}
            >
              <div className="flex items-center gap-2">
                <span className="!text-[12px] !font-[500] !text-[#000000] border border-[#2A35484D] rounded-[4px] w-[24px] h-[24px] flex items-center justify-center">
                  {selected.length}
                </span>
                <span className="!font-[500]">Selected</span>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={allSelectableSelected}
                      onChecked={handleSelectAll}
                      divClassName="flex items-center"
                      UncheckedIcon={UncheckedGreyIcon}
                      checkClassName={"!w-[22px] !h-[22px]"}
                    />
                    <span className="whitespace-nowrap !font-[500]">
                      Select All
                    </span>
                  </div>
                  {showPendingOnly ? (
                    <button
                      onClick={handleBulkPending}
                      className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap px-4 py-1 rounded-[6px]"
                    >
                      Pending Offers
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleBulkApprove}
                        className="bg-[#00ADEF] text-white h-[38px] whitespace-nowrap pl-4 pr-8 py-1 rounded-[6px] flex items-center gap-2"
                      >
                        <img
                          src={whiteTick}
                          alt="tick"
                          className="h-[16px] w-[16px]"
                        />
                        Approve Offers
                      </button>
                      <button
                        onClick={handleBulkDecline}
                        className="border border-[#00ADEF] text-[#00ADEF] h-[38px] px-4 py-1 rounded-[6px] whitespace-nowrap"
                      >
                        Decline Offers
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelected([]);
                    setSelectedMerchantIds([]);
                  }}
                  className="w-[24px] h-[24px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
                  title="Clear selection"
                >
                  <img src={blueCross} alt="Close" className="w-[19px] h-[19px]" />
                </button>
              </div>
            </div>
          )
        }
      </div>
    </>
  );
};

export default AdminOfferlist;